import { useEffect } from 'react';
import MilitaryHero from './components/MilitaryHero';
import DealershipContrast from './components/DealershipContrast';
import HowItWorks from './components/HowItWorks';
import Eligibility from './components/Eligibility';
import CharityOption from './components/CharityOption';
import ShareDiscountBanner from './components/ShareDiscountBanner';
import InquiryForm from './components/InquiryForm';
import MilitaryFaq from './components/MilitaryFaq';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { CORE_BUSINESS } from '../../lib/coreSchemas';
import { useSEOHead } from '../../hooks/useSEOHead';

function useMilitaryDiscountSchemas() {
  useEffect(() => {
    // ── Schema 1: SpecialAnnouncement — surfaces in Google rich results ──
    injectJsonLd('schema-military-announcement', {
      '@context': 'https://schema.org',
      '@type': 'SpecialAnnouncement',
      '@id': `${CORE_BUSINESS.url}/military-discount#announcement`,
      name: 'Dog Tag Discount — $300 Off for Military, Veterans & First Responders',
      text: 'Active duty military, veterans, military families, police officers, firefighters, EMTs, paramedics, and 911 dispatchers receive $300 off any vehicle purchase at Rockhound Auto. No DD-214 or badge number required — honor system only. The $300 may alternatively be donated to Oregon Dog Rescue.',
      datePosted: '2025-01-01',
      expires: '2026-12-31',
      category: 'https://www.wikidata.org/wiki/Q3966506',
      announcementLocation: {
        '@type': 'AutoDealer',
        name: CORE_BUSINESS.name,
        url: CORE_BUSINESS.url,
        address: {
          '@type': 'PostalAddress',
          streetAddress: CORE_BUSINESS.address.streetAddress,
          addressLocality: CORE_BUSINESS.address.addressLocality,
          addressRegion: CORE_BUSINESS.address.addressRegion,
          postalCode: CORE_BUSINESS.address.postalCode,
          addressCountry: 'US',
        },
      },
      spatialCoverage: {
        '@type': 'Place',
        name: 'Oregon Coast and SW Washington',
      },
    });

    // ── Schema 2: Offer — enables price/discount rich results ──
    injectJsonLd('schema-military-offer', {
      '@context': 'https://schema.org',
      '@type': 'Offer',
      '@id': `${CORE_BUSINESS.url}/military-discount#offer`,
      name: 'Dog Tag Discount — $300 Off Any Vehicle',
      description: '$300 discount on any used vehicle purchase for active duty military, veterans, military families, police, firefighters, EMTs, paramedics, and 911 dispatchers. No documentation required — honor system.',
      url: `${CORE_BUSINESS.url}/military-discount`,
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'PriceSpecification',
        price: -300,
        priceCurrency: 'USD',
        description: '$300 off the purchase price of any vehicle',
      },
      eligibleCustomerType: [
        'https://schema.org/MilitaryOrCivilian',
      ],
      validFrom: '2025-01-01',
      validThrough: '2026-12-31',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'AutoDealer',
        name: CORE_BUSINESS.name,
        url: CORE_BUSINESS.url,
        telephone: CORE_BUSINESS.telephone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: CORE_BUSINESS.address.streetAddress,
          addressLocality: CORE_BUSINESS.address.addressLocality,
          addressRegion: CORE_BUSINESS.address.addressRegion,
          postalCode: CORE_BUSINESS.address.postalCode,
          addressCountry: 'US',
        },
      },
    });

    // ── Schema 3: FAQPage — boosts FAQ rich results for military questions ──
    injectJsonLd('schema-military-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${CORE_BUSINESS.url}/military-discount#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Who qualifies for the Dog Tag Discount?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Active duty military, veterans, military spouses and dependents, National Guard, Reserves, police officers, firefighters, EMTs, paramedics, and 911 dispatchers all qualify for the $300 Dog Tag Discount at Rockhound Auto.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do police officers and firefighters qualify for the military discount?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Police officers, firefighters, fire & rescue personnel, EMTs, paramedics, and 911 dispatchers are all fully eligible for the $300 Dog Tag Discount at Rockhound Auto. No badge number or documentation required.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do I need to show a DD-214 or badge to get the discount?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Rockhound Auto operates on an honor system. No DD-214, badge number, or any documentation is required to receive the $300 Dog Tag Discount.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I donate the $300 discount instead of keeping it?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Instead of taking the $300 off your purchase price, you can choose to donate the full $300 to Oregon Dog Rescue on your behalf.',
          },
        },
      ],
    });

    return () => {
      removeJsonLd('schema-military-announcement');
      removeJsonLd('schema-military-offer');
      removeJsonLd('schema-military-faq');
    };
  }, []);
}

export default function DogTagDiscountPage() {
  useMilitaryDiscountSchemas();

  useSEOHead({
    title: 'Dog Tag Discount — $300 Off Military & First Responders',
    description: 'Active duty, veterans, police, firefighters, EMTs & first responders get $300 off any vehicle at Rockhound Auto — or donate it to Oregon Dog Rescue. No DD-214 required. Warrenton, Oregon.',
    keywords: 'military discount Oregon, first responder car discount, veteran car discount, police discount used cars, firefighter discount Oregon, Warrenton military discount',
    canonicalUrl: 'https://rockhoundauto.com/military-discount',
    geoRegion: 'US-OR',
    geoPlacename: 'Warrenton, Oregon',
    geoPosition: '46.1651;-123.9237',
  });

  return (
    <>
      <MilitaryHero />
      <DealershipContrast />
      <HowItWorks />
      <Eligibility />
      <CharityOption />
      <ShareDiscountBanner />
      <InquiryForm />
      <MilitaryFaq />
    </>
  );
}
